import type { SupabaseClient } from "@supabase/supabase-js";

export type ConsoleModuleKey = "admin_branding";

export interface AccountConsoleModules {
  adminBranding: boolean;
}

export type ConsoleModuleWriteOutcome =
  | { ok: true; enabled: boolean }
  | { ok: false; reason: string };

export const SAFE_CONSOLE_MODULE_WRITE_FAILURE =
  "We could not save that Console module change. Refresh Capabilities before trying again.";

const KNOWN_CONSOLE_MODULE_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to change Console modules.",
  "Platform superadmin access is required to change Console modules.",
  "Account id is required to change Console modules.",
  "Console module key is required.",
  "Console module enabled state is required.",
  "Account does not exist.",
]);

function mapConsoleModules(data: unknown): AccountConsoleModules | null {
  if (!Array.isArray(data)) {
    return null;
  }

  let adminBranding: boolean | null = null;
  for (const item of data) {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
      return null;
    }
    const row = item as Record<string, unknown>;
    if (row.module_key === "admin_branding") {
      if (adminBranding !== null || typeof row.enabled !== "boolean") {
        return null;
      }
      adminBranding = row.enabled;
    }
  }
  return adminBranding === null ? null : { adminBranding };
}

function isExactConsoleModuleReceipt(
  data: unknown,
  accountId: string,
  moduleKey: ConsoleModuleKey,
  enabled: boolean,
): boolean {
  if (!Array.isArray(data) || data.length !== 1) {
    return false;
  }
  const row = data[0];
  return typeof row === "object"
    && row !== null
    && !Array.isArray(row)
    && (row as Record<string, unknown>).account_id === accountId
    && (row as Record<string, unknown>).module_key === moduleKey
    && (row as Record<string, unknown>).enabled === enabled;
}

export async function fetchAccountConsoleModules(
  client: SupabaseClient,
  accountId: string,
): Promise<AccountConsoleModules> {
  const { data, error } = await client.rpc("get_account_console_modules", {
    p_account_id: accountId,
  });
  const modules = mapConsoleModules(data);
  if (error !== null || modules === null) {
    throw new Error("Could not load Console modules.");
  }
  return modules;
}

export async function setPlatformConsoleModule(
  client: SupabaseClient,
  accountId: string,
  moduleKey: ConsoleModuleKey,
  enabled: boolean,
): Promise<ConsoleModuleWriteOutcome> {
  try {
    const { data, error } = await client.rpc("platform_set_console_module", {
      p_account_id: accountId,
      p_module_key: moduleKey,
      p_enabled: enabled,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_CONSOLE_MODULE_FAILURES.has(message)
          ? message
          : SAFE_CONSOLE_MODULE_WRITE_FAILURE,
      };
    }

    if (!isExactConsoleModuleReceipt(data, accountId, moduleKey, enabled)) {
      return { ok: false, reason: SAFE_CONSOLE_MODULE_WRITE_FAILURE };
    }
    return { ok: true, enabled };
  } catch {
    return { ok: false, reason: SAFE_CONSOLE_MODULE_WRITE_FAILURE };
  }
}
